import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type FetchResponse, type BrowserSession, type CookieJar } from '@/api/client'

const SAMPLES: Record<string, object> = {
  basic: {
    url: 'https://example.com',
    wait_for: 'selector:h1',
    viewport: { w: 1920, h: 1080 },
    extract: {
      fields: { heading: 'h1|text', link: 'a|attr:href' },
      screenshot: true,
      screenshot_full_page: true,
    },
    timeout_ms: 20000,
  },
  login: {
    url: 'https://login.example.com/',
    wait_for: 'selector:#username',
    cookie_jar: 'my-site',
    actions: [
      { type: 'fill', selector: '#username', text: 'my-user@example.com' },
      { type: 'fill', selector: '#password', text: 'my-password' },
      { type: 'click', selector: 'button[type=submit]' },
      { type: 'wait_selector', selector: '.logged-in-marker' },
      { type: 'wait', ms: 1000 },
    ],
    viewport: { w: 1920, h: 1080 },
    extract: { screenshot: true, screenshot_full_page: false },
    timeout_ms: 30000,
  },
  withJar: {
    url: 'https://www.example.com/account',
    cookie_jar: 'my-site',
    wait_for: 'selector:body',
    viewport: { w: 1920, h: 1080 },
    extract: {
      fields: { email: '.user-email|text' },
      screenshot: true,
      screenshot_full_page: true,
    },
    timeout_ms: 20000,
  },
}

const SAMPLE_REQUEST = SAMPLES.basic

export default function Test() {
  const [body, setBody] = useState(() => JSON.stringify(SAMPLE_REQUEST, null, 2))
  const [resp, setResp] = useState<FetchResponse | null>(null)

  const run = useMutation({
    mutationFn: async () => {
      const req = JSON.parse(body)
      return api.browser.test(req)
    },
    onSuccess: (r) => setResp(r),
  })

  return (
    <div className="space-y-4">
      <LiveSessions />

      <section className="border border-border rounded-lg p-4 bg-card text-card-foreground">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-muted-foreground">Run fetch</h2>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">samples:</span>
            {Object.keys(SAMPLES).map((k) => (
              <button
                key={k}
                className="text-primary underline hover:opacity-80 transition-opacity"
                onClick={() => setBody(JSON.stringify(SAMPLES[k], null, 2))}
              >
                {k}
              </button>
            ))}
          </div>
        </div>
        <textarea
          className="w-full h-60 border border-border rounded px-3 py-2 font-mono text-xs bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="mt-2 flex items-center gap-3">
          <button
            onClick={() => run.mutate()}
            disabled={run.isPending}
            className="bg-primary text-primary-foreground px-4 py-2 rounded disabled:opacity-50 hover:opacity-90 transition-opacity"
          >
            {run.isPending ? 'Running…' : 'Run'}
          </button>
          {run.error && (
            <span className="text-sm text-destructive">{(run.error as Error).message}</span>
          )}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Actions: <code>click</code>, <code>type</code>, <code>fill</code> (clear+type),{' '}
          <code>select</code>, <code>check</code>/<code>uncheck</code>, <code>press</code>,{' '}
          <code>wait_selector</code>, <code>wait</code>, <code>scroll</code>,{' '}
          <code>navigate</code>, <code>mouse_move</code>, <code>evaluate</code>.
        </p>
      </section>

      <CookieJars />

      {resp && <Result resp={resp} />}
    </div>
  )
}

function CookieJars() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['jars'], queryFn: api.browser.jars, refetchInterval: 5000 })
  const del = useMutation({
    mutationFn: (name: string) => api.browser.jarDelete(name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jars'] }),
  })
  const items = q.data?.items ?? []
  return (
    <section className="border border-border rounded-lg p-4 bg-card text-card-foreground">
      <h2 className="text-sm font-semibold text-muted-foreground mb-3">Cookie jars</h2>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No jars. Add <code>cookie_jar: "any-name"</code> to a request to start one.
        </p>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[480px]">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-2">Name</th>
              <th>Cookies</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((j: CookieJar) => (
              <tr key={j.name} className="border-t border-border">
                <td className="py-2 font-mono">{j.name}</td>
                <td>{j.count}</td>
                <td className="text-xs text-muted-foreground">
                  {new Date(j.updated_at * 1000).toLocaleString()}
                </td>
                <td className="text-right">
                  <button
                    onClick={() => confirm(`Clear jar "${j.name}"?`) && del.mutate(j.name)}
                    className="text-xs px-2 py-1 border border-border rounded text-destructive hover:bg-destructive-soft transition-colors"
                  >
                    Clear
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </section>
  )
}

function Result({ resp }: { resp: FetchResponse }) {
  const { screenshot_b64, ...rest } = resp
  return (
    <section className="border border-border rounded-lg p-4 bg-card text-card-foreground space-y-3">
      <h2 className="text-sm font-semibold text-muted-foreground">Result</h2>
      <div className="text-sm">
        {resp.ok ? (
          <span className="text-success">ok</span>
        ) : (
          <span className="text-destructive">failed</span>
        )}{' '}
        · <code>{resp.http_status ?? '—'}</code> ·{' '}
        <span className="text-muted-foreground">{resp.timing.total_ms}ms</span>
      </div>
      {screenshot_b64 && (
        <img
          src={`data:image/png;base64,${screenshot_b64}`}
          alt="screenshot"
          className="max-w-full border border-border rounded"
        />
      )}
      <details>
        <summary className="text-xs text-muted-foreground cursor-pointer">Raw response</summary>
        <pre className="mt-2 text-xs font-mono overflow-auto bg-muted text-foreground p-2 rounded max-h-96">
          {JSON.stringify(rest, null, 2)}
        </pre>
      </details>
    </section>
  )
}

function LiveSessions() {
  const q = useQuery({
    queryKey: ['browser-sessions'],
    queryFn: api.browser.sessions,
    refetchInterval: 1500,
  })
  const items = q.data?.items ?? []

  return (
    <section className="border border-border rounded-lg p-4 bg-card text-card-foreground">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-muted-foreground">Live browsers</h2>
        <span className="text-xs text-muted-foreground">in-flight: {items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No active fetches.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((s) => (
            <SessionCard key={s.id} s={s} />
          ))}
        </div>
      )}
    </section>
  )
}

function SessionCard({ s }: { s: BrowserSession }) {
  // bust cache so the <img> refetches on interval
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1500)
    return () => clearInterval(t)
  }, [])
  const elapsed = Math.round((Date.now() - new Date(s.started_at).getTime()) / 100) / 10
  return (
    <div className="border border-border rounded p-2 text-xs space-y-1 bg-card text-card-foreground">
      <div className="font-mono truncate" title={s.url}>
        {s.url}
      </div>
      <div className="text-muted-foreground">
        {elapsed}s · <span className="font-mono">{s.id.slice(0, 8)}</span>
      </div>
      <img
        src={`/api/admin/browser/sessions/${encodeURIComponent(s.id)}/screenshot?ts=${tick}`}
        alt="tab screenshot"
        className="w-full border border-border rounded bg-muted"
        onError={(e) => {
          // Session ended mid-refresh — hide broken icon.
          ;(e.currentTarget as HTMLImageElement).style.visibility = 'hidden'
        }}
      />
    </div>
  )
}
